package com.flowyride.feature.planner

import com.flowyride.core.domain.model.RouteEngineId
import com.flowyride.core.domain.model.RoutePreferences

/** The ride as it was before an edit: restoring it shows its route again without asking the engine. */
data class RideSnapshot(
    val stops: List<StopUiState>,
    val preferences: RoutePreferences,
    val selectedEngineId: RouteEngineId?,
    val route: RouteUiState,
)

/**
 * Undo and redo of the edits of one ride ([generation] = [PlannerUiState.rideGeneration]): a ride loaded or
 * generated afterwards starts a fresh history.
 */
data class RideHistory(
    val generation: Int = 0,
    val undo: List<RideSnapshot> = emptyList(),
    val redo: List<RideSnapshot> = emptyList(),
) {
    /** A new edit of the ride of [generation]: [before] can be restored, what was undone cannot be redone anymore. */
    fun edited(before: RideSnapshot, generation: Int): RideHistory {
        val undo = if (generation == this.generation) undo else emptyList()
        return RideHistory(generation, (undo + before).takeLast(MAX_STEPS), redo = emptyList())
    }

    companion object {
        const val MAX_STEPS = 50
    }
}

internal fun PlannerUiState.snapshot() = RideSnapshot(stops, preferences, selectedEngineId, route)

/** What an edit must change to be worth undoing: the stops (not their names), their order and the route settings. */
internal fun PlannerUiState.rideKey() = Triple(stops.map { it.id to it.selected?.point }, preferences, selectedEngineId)
